import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { 
  Phone, 
  Mail, 
  MapPin, 
  ArrowLeft,
  CheckCircle,
  ArrowRight
} from "lucide-react";

// Brand Information
const BRAND = {
  name: "Economy Accounting",
  fullName: "Economy Accounting and Tax Services",
  email: "sales@economyaccounting.ca",
  locations: {
    edmonton: {
      city: "Edmonton",
      address: "9190 34a Ave NW unit # 107, Edmonton, AB T6E 5P4",
      phones: ["647-772-4857", "780-522-2220"],
      email: "sales@economyaccounting.ca"
    },
    yellowknife: {
      city: "Yellowknife",
      address: "850 Dusseault Court, Yellowknife, NT X1A 2Z4",
      phones: ["867-444-6556"],
      email: "info@economyaccounting.ca"
    }
  }
};

// Service Data
const servicesData = {
  "accounting": {
    title: "Accounting",
    heroImage: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1600&q=80",
    shortDescription: "Comprehensive financial reporting and analysis to ensure compliance and support strategic business decisions.",
    fullDescription: "Our accounting services provide comprehensive financial reporting and analysis that goes beyond basic compliance. We help businesses of all sizes maintain accurate financial records, prepare detailed reports, and gain insights that support strategic decision-making. Our team of experienced CPAs ensures your financial statements meet all regulatory requirements while providing valuable business intelligence.",
    benefits: [
      "Monthly and annual financial statement preparation",
      "Management reporting and analysis",
      "Budget preparation and variance analysis",
      "Cash flow management and forecasting",
      "Financial compliance and regulatory reporting",
      "Year-end adjustments and reconciliations"
    ],
    process: [
      { step: "Assessment", desc: "Review your current financial processes and needs" },
      { step: "Setup", desc: "Implement proper accounting systems and procedures" },
      { step: "Execute", desc: "Manage ongoing financial recording and reporting" },
      { step: "Report", desc: "Deliver insights and recommendations regularly" }
    ],
    faqs: [
      { q: "How often will I receive financial reports?", a: "We provide monthly financial statements and can customize reporting frequency based on your needs." },
      { q: "Do you work with specific accounting software?", a: "We work with all major accounting platforms including QuickBooks, Xero, Sage, and more." },
      { q: "Can you help with historical accounting cleanup?", a: "Yes, we specialize in cleaning up and organizing historical financial records." }
    ]
  },
  "bookkeeping": {
    title: "Bookkeeping",
    heroImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1600&q=80",
    shortDescription: "Accurate recording and management of daily financial transactions for streamlined operations and clarity.",
    fullDescription: "Our bookkeeping services provide the foundation for sound financial management. We handle the day-to-day recording of your financial transactions, ensuring accuracy and timeliness so you always have a clear picture of your business finances. Our team uses modern cloud-based accounting software to give you real-time access to your financial data.",
    benefits: [
      "Accurate and timely recording of all transactions",
      "Bank and credit card reconciliations",
      "Accounts payable and receivable management",
      "Monthly financial statements and reports",
      "Cloud-based access to your books anytime",
      "Integration with your existing business systems"
    ],
    process: [
      { step: "Setup", desc: "Configure your accounting system and chart of accounts" },
      { step: "Record", desc: "Process transactions and maintain accurate records" },
      { step: "Reconcile", desc: "Regular reconciliation of all accounts" },
      { step: "Report", desc: "Deliver monthly reports and financial insights" }
    ],
    faqs: [
      { q: "What software do you use?", a: "We work with QuickBooks, Xero, Wave, and other popular accounting platforms based on your preference." },
      { q: "How often will my books be updated?", a: "We offer weekly, bi-weekly, or monthly bookkeeping services based on your transaction volume and needs." },
      { q: "Can you clean up messy books?", a: "Yes, we specialize in bookkeeping cleanup and can get your records organized and accurate." }
    ]
  },
  "business-setup": {
    title: "Business Setup",
    heroImage: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=1600&q=80",
    shortDescription: "Expert guidance to establish your business with solid financial and legal foundations.",
    fullDescription: "Starting a business is exciting, but getting the financial and legal foundations right is crucial for long-term success. Our business setup services guide entrepreneurs through every step of establishing their business, from choosing the right business structure to setting up proper accounting systems and ensuring compliance with all regulatory requirements.",
    benefits: [
      "Business structure consultation (sole proprietorship, corporation, partnership)",
      "Corporate registration and incorporation",
      "CRA business number and tax account registration",
      "Accounting system setup and configuration",
      "Initial compliance and regulatory guidance",
      "Banking and financial account setup assistance"
    ],
    process: [
      { step: "Consult", desc: "Discuss your business goals and recommend optimal structure" },
      { step: "Register", desc: "Handle all incorporation and registration paperwork" },
      { step: "Setup", desc: "Configure accounting systems and compliance processes" },
      { step: "Launch", desc: "Ensure you're ready to start operating smoothly" }
    ],
    faqs: [
      { q: "Should I incorporate my business?", a: "It depends on your situation. We'll help you understand the tax and liability implications of each structure." },
      { q: "How long does business setup take?", a: "Basic setup can be completed within 1-2 weeks, while incorporation typically takes 2-4 weeks." },
      { q: "Do you help with business plans?", a: "While we focus on financial setup, we can refer you to partners for business planning assistance." }
    ]
  },
  "tax-planning": {
    title: "Tax Planning",
    heroImage: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1600&q=80",
    shortDescription: "Strategic tax solutions to minimize liabilities and maximize savings while ensuring compliance.",
    fullDescription: "Effective tax planning is essential for both individuals and businesses. Our tax planning services go beyond simple tax preparation—we develop comprehensive strategies to legally minimize your tax burden while ensuring full compliance with CRA regulations. We stay current with tax law changes to identify every opportunity for savings.",
    benefits: [
      "Personal and corporate tax return preparation",
      "Strategic tax planning and optimization",
      "Tax credits and incentives identification (SR&ED, IRAP, etc.)",
      "RRSP and TFSA optimization strategies",
      "Estate and succession tax planning",
      "CRA audit support and representation"
    ],
    process: [
      { step: "Review", desc: "Analyze your current tax situation and history" },
      { step: "Plan", desc: "Develop tax-saving strategies tailored to your needs" },
      { step: "Implement", desc: "Execute strategies and prepare accurate returns" },
      { step: "Monitor", desc: "Ongoing review and adjustment for optimal results" }
    ],
    faqs: [
      { q: "When should I start tax planning?", a: "Tax planning is most effective when done throughout the year, not just at tax time." },
      { q: "Can you help with past unfiled returns?", a: "Yes, we can help file multiple years of returns and work with CRA on any issues." },
      { q: "Do you handle corporate and personal taxes?", a: "Yes, we provide comprehensive tax services for both businesses and individuals." }
    ]
  },
  "payroll": {
    title: "Payroll",
    heroImage: "https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=1600&q=80",
    shortDescription: "Efficient payroll processing with precise calculations and timely disbursements.",
    fullDescription: "Our payroll services take the complexity out of paying your employees. We handle all aspects of payroll processing, from calculating wages and deductions to filing remittances and year-end reporting. Our accurate, timely service ensures your employees are paid correctly and all government requirements are met.",
    benefits: [
      "Accurate payroll calculations every pay period",
      "Direct deposit and pay stub generation",
      "Government remittance filings (CPP, EI, income tax)",
      "T4 and ROE preparation and filing",
      "Workers' compensation reporting",
      "Vacation and benefit tracking"
    ],
    process: [
      { step: "Setup", desc: "Configure payroll system with employee information" },
      { step: "Process", desc: "Calculate and process payroll each pay period" },
      { step: "Remit", desc: "File and pay all government remittances" },
      { step: "Report", desc: "Provide payroll reports and year-end filings" }
    ],
    faqs: [
      { q: "How often can payroll be processed?", a: "We support weekly, bi-weekly, semi-monthly, and monthly payroll frequencies." },
      { q: "Can you handle contractors as well?", a: "Yes, we can manage contractor payments and T4A preparation alongside regular employee payroll." },
      { q: "What about employees in multiple provinces?", a: "We handle multi-province payroll including different provincial tax rates and requirements." }
    ]
  },
  "wealth-management": {
    title: "Wealth Management",
    heroImage: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1600&q=80",
    shortDescription: "Tailored financial strategies to grow, preserve, and manage your wealth effectively.",
    fullDescription: "Our wealth management services help individuals and families build, protect, and grow their wealth over time. We take a holistic approach to financial planning, considering your complete financial picture including investments, tax implications, retirement planning, and estate considerations to develop strategies that align with your goals.",
    benefits: [
      "Comprehensive financial planning",
      "Investment strategy consultation",
      "Retirement planning and RRSP optimization",
      "Estate planning and wealth transfer strategies",
      "Tax-efficient investment structuring",
      "Risk management and insurance review"
    ],
    process: [
      { step: "Discover", desc: "Understand your financial goals and current situation" },
      { step: "Analyze", desc: "Comprehensive review of your financial position" },
      { step: "Strategize", desc: "Develop customized wealth management plan" },
      { step: "Execute", desc: "Implement strategies and provide ongoing guidance" }
    ],
    faqs: [
      { q: "What's the minimum investment to work with you?", a: "We work with clients at all wealth levels and can tailor services to your needs." },
      { q: "Do you manage investments directly?", a: "We provide strategic advice and work with trusted investment partners for implementation." },
      { q: "How often will we meet?", a: "We recommend quarterly reviews, with more frequent meetings during major life changes." }
    ]
  }
};

const ServicePage = () => {
  const { serviceSlug } = useParams();
  const service = servicesData[serviceSlug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceSlug]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F6F8]">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-[#1A2744] mb-4">Service Not Found</h1>
          <Link to="/" className="text-[#C8973A] hover:underline">Return to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid={`service-page-${serviceSlug}`}>
      {/* Navigation */}
      <nav className="bg-[#1A2744] fixed w-full z-50" data-testid="service-nav">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center" data-testid="service-logo">
            <span className="font-serif text-2xl font-bold text-[#C8973A]">Economy</span>
            <span className="font-serif text-2xl font-bold text-white"> Accounting</span>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-white hover:text-[#C8973A] transition-colors flex items-center gap-2">
              <ArrowLeft size={18} />
              Back to Home
            </Link>
            <a 
              href={`tel:${BRAND.phone}`}
              className="bg-[#C8973A] text-white px-6 py-3 font-medium text-sm uppercase tracking-wide hover:bg-[#B3852E] transition-colors"
              data-testid="service-call-btn"
            >
              Call Us
            </a>
          </div>
          <Link to="/" className="md:hidden text-white">
            <ArrowLeft size={24} />
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 min-h-[50vh] flex items-center" data-testid="service-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${service.heroImage}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A2744]/95 via-[#1A2744]/80 to-[#1A2744]/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20">
          <div className="max-w-2xl">
            <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#C8973A] font-semibold mb-4 block">
              Our Services
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
              {service.title}
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              {service.shortDescription}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-white" data-testid="service-content">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="font-serif text-3xl text-[#1A2744] mb-6">Overview</h2>
              <p className="text-[#3D3D3D] text-lg leading-relaxed mb-10">
                {service.fullDescription}
              </p>

              {/* Benefits */}
              <h3 className="font-serif text-2xl text-[#1A2744] mb-6">What's Included</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                {service.benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#C8973A] flex-shrink-0 mt-1" />
                    <span className="text-[#3D3D3D]">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Process */}
              <h3 className="font-serif text-2xl text-[#1A2744] mb-6">Our Process</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {service.process.map((item, index) => (
                  <div key={item.step} className="bg-[#F5F6F8] p-6 border-l-4 border-[#C8973A]">
                    <div className="text-[#C8973A] font-bold text-sm mb-2">STEP {index + 1}</div>
                    <h4 className="font-serif text-xl text-[#1A2744] mb-2">{item.step}</h4>
                    <p className="text-[#6B7280] text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* FAQs */}
              <h3 className="font-serif text-2xl text-[#1A2744] mb-6">Frequently Asked Questions</h3>
              <div className="space-y-6">
                {service.faqs.map((faq) => (
                  <div key={faq.q} className="border-b border-[#E5E7EB] pb-6">
                    <h4 className="font-semibold text-[#1A2744] mb-2">{faq.q}</h4>
                    <p className="text-[#6B7280]">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* CTA Card */}
              <div className="bg-[#1A2744] p-8 mb-8 sticky top-24" data-testid="service-cta-card">
                <h3 className="font-serif text-2xl text-white mb-4">Ready to Get Started?</h3>
                <p className="text-white/70 mb-6">
                  Contact us today for a free consultation and discover how we can help your business.
                </p>
                <Link 
                  to="/#contact"
                  className="block w-full bg-[#C8973A] text-white py-4 text-center font-medium text-sm uppercase tracking-wide hover:bg-[#B3852E] transition-colors mb-4"
                  data-testid="service-contact-btn"
                >
                  Request a Quote
                </Link>
                <a 
                  href={`tel:${BRAND.locations.edmonton.phones[0]}`}
                  className="block w-full border-2 border-white text-white py-4 text-center font-medium text-sm uppercase tracking-wide hover:bg-white hover:text-[#1A2744] transition-colors"
                >
                  Call Us
                </a>
              </div>

              {/* Contact Info - Both Locations */}
              <div className="bg-[#F5F6F8] p-6 space-y-6">
                <div>
                  <h4 className="font-semibold text-[#1A2744] mb-3 text-sm">Edmonton Office</h4>
                  <div className="space-y-2 text-[#6B7280] text-sm">
                    <p>{BRAND.locations.edmonton.address}</p>
                    {BRAND.locations.edmonton.phones.map((phone) => (
                      <a key={phone} href={`tel:${phone}`} className="hover:text-[#C8973A] block">{phone}</a>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-[#1A2744] mb-3 text-sm">Yellowknife Office</h4>
                  <div className="space-y-2 text-[#6B7280] text-sm">
                    <p>{BRAND.locations.yellowknife.address}</p>
                    <a href={`tel:${BRAND.locations.yellowknife.phones[0]}`} className="hover:text-[#C8973A] block">
                      {BRAND.locations.yellowknife.phones[0]}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-[#F5F6F8]" data-testid="other-services">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-3xl text-[#1A2744] text-center mb-12">Explore Other Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Object.entries(servicesData)
              .filter(([slug]) => slug !== serviceSlug)
              .slice(0, 3)
              .map(([slug, svc]) => (
                <Link 
                  key={slug}
                  to={`/services/${slug}`}
                  className="bg-white p-8 border border-[#E5E7EB] hover:border-[#C8973A] hover:shadow-lg transition-all group"
                >
                  <h3 className="font-serif text-xl text-[#1A2744] mb-2 group-hover:text-[#C8973A] transition-colors">
                    {svc.title}
                  </h3>
                  <p className="text-[#6B7280] text-sm mb-4">{svc.shortDescription}</p>
                  <span className="text-[#C8973A] text-sm font-medium inline-flex items-center gap-1">
                    Learn More <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-[#C8973A] py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1A2744] mb-4">
            Let's Discuss Your {service.title} Needs
          </h2>
          <p className="text-[#1A2744]/80 text-lg mb-8">
            Schedule a free consultation with our experts today.
          </p>
          <Link 
            to="/#contact"
            className="inline-block bg-[#1A2744] text-white px-8 py-4 font-medium text-sm uppercase tracking-wide hover:bg-[#0f1829] transition-colors"
          >
            Contact Us Now
          </Link>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="bg-[#1A2744] py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/60 text-sm">
            © 2025 {BRAND.name}. All Rights Reserved.
          </p>
          <Link to="/" className="text-white/60 hover:text-[#C8973A] text-sm">
            Return to Home
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default ServicePage;
